// Hooks del cobro en tienda: opciones del crédito, cobrar con llave por intento y qué hacer por code.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { ApiError } from '@/api/errors';
import {
    createSellerPayment,
    getSellerPaymentOptions,
    type SellerPaymentOptions,
    type SellerPaymentResult,
} from '@/api/services/loan-credit';
import { generateIdempotencyKey } from '@/lib/idempotency';
import { chargeFingerprintOf, chargeRequestOf, type ChargeDraft } from '../lib/chargeFlow';
import {
    chargeSignatureOf,
    dropPendingCharge,
    newPendingCharge,
    pendingChargeOf,
    savePendingCharge,
} from '../lib/pendingCharges';
import {
    CHARGE_ERROR_FALLBACK,
    CHARGE_ERROR_MESSAGE,
    CHARGE_UNCERTAIN_MESSAGE,
} from '../lib/collectionsFormat';
import { COLLECTIONS_KEYS } from './useCollections';

/** Qué hace la pantalla con el error: recargar opciones, reintentar, corregir, llave nueva o detenerse. */
export type ChargeErrorAction = 'reload' | 'retry' | 'fix' | 'newKey' | 'stop';

const ACTION_BY_CODE: Record<string, ChargeErrorAction> = {
    CUSTOMER_PAYMENT_AMOUNT_CHANGED: 'reload',
    PAYMENT_OPTION_UNAVAILABLE: 'reload',
    IN_STORE_PAYMENT_COLLECTION_IN_FLIGHT: 'retry',
    AD_HOC_CHARGE_PENDING: 'retry',
    CREDIT_PLAN_BATCHES_PENDING: 'retry',
    CREDIT_OPERATION_IN_PROGRESS: 'retry',
    COLLECTION_ENGINE_UNAVAILABLE: 'retry',
    SELLER_IDENTITY_LOOKUP_FAILED: 'retry',
    STORE_ROSTER_LOOKUP_FAILED: 'retry',
    INVALID_PAYMENT_DETAIL: 'fix',
    IDEMPOTENCY_KEY_MISMATCH: 'newKey',
};

const AD_HOC_CHARGE_PENDING = 'AD_HOC_CHARGE_PENDING';
/** Cuántos 409 `AD_HOC_CHARGE_PENDING` seguidos con la misma llave antes de ofrecer una nueva. */
export const PENDING_REPEATS_BEFORE_RESTART = 3;

const codeOf = (error: unknown): string => (error instanceof ApiError ? error.code : '');

const SERVER_ERROR_STATUS = 500;

/** Timeout, red caída o 5xx sin code conocido: no se sabe si el cobro se asentó. */
function isUncertain(error: unknown): boolean {
    if (!(error instanceof ApiError)) return true;
    return error.httpStatus >= SERVER_ERROR_STATUS && !Object.hasOwn(ACTION_BY_CODE, error.code);
}

export function chargeErrorOf(error: unknown): { action: ChargeErrorAction; message: string } {
    if (isUncertain(error)) return { action: 'retry', message: CHARGE_UNCERTAIN_MESSAGE };
    const code = codeOf(error);
    return {
        action: Object.hasOwn(ACTION_BY_CODE, code) ? ACTION_BY_CODE[code] : 'stop',
        message: Object.hasOwn(CHARGE_ERROR_MESSAGE, code)
            ? CHARGE_ERROR_MESSAGE[code]
            : CHARGE_ERROR_FALLBACK,
    };
}

export const sellerPaymentOptionsKey = (creditId: string) =>
    [...COLLECTIONS_KEYS.all, 'payment-options', creditId] as const;

/** Opciones de cobro del crédito (fuente del monto que se confirma). */
export function useSellerPaymentOptions(creditId: string | null) {
    return useQuery({
        queryKey: sellerPaymentOptionsKey(creditId ?? ''),
        queryFn: () => getSellerPaymentOptions(creditId as string),
        enabled: creditId !== null,
        retry: false,
    });
}

export interface ChargeVariables {
    options: SellerPaymentOptions;
    draft: ChargeDraft;
    /** «Volver a intentar»: el intento anterior se abandona y este lleva llave nueva. */
    forceNewKey?: boolean;
}

/** Cobrar: el mismo cuerpo dentro de 10 minutos reusa su llave (réplica segura); otro cuerpo o `forceNewKey` estrena. */
export function useCreateSellerPayment(creditId: string) {
    const queryClient = useQueryClient();
    const [pendingRepeats, setPendingRepeats] = useState(0);
    const [, setPendingVersion] = useState(0);
    const charge = useMutation<SellerPaymentResult, unknown, ChargeVariables>({
        mutationFn: ({ options, draft, forceNewKey }) => {
            const signature = chargeSignatureOf(options, draft);
            let pending = forceNewKey ? null : pendingChargeOf(creditId);
            if (pending?.signature !== signature) {
                pending = newPendingCharge(generateIdempotencyKey(), options, draft);
                savePendingCharge(creditId, pending);
                setPendingRepeats(0);
            }
            return createSellerPayment(
                creditId,
                chargeRequestOf(pending.options, pending.draft, pending.key),
            );
        },
        onSuccess: () => {
            dropPendingCharge(creditId);
            setPendingRepeats(0);
            void queryClient.invalidateQueries({ queryKey: COLLECTIONS_KEYS.all });
        },
        onError: (error) => {
            const { action } = chargeErrorOf(error);
            if (action === 'newKey' || action === 'reload') dropPendingCharge(creditId);
            setPendingRepeats((count) => (codeOf(error) === AD_HOC_CHARGE_PENDING ? count + 1 : 0));
        },
    });
    const pendingCharge = pendingChargeOf(creditId);
    /** Las opciones del cobro sin confirmar para esta elección: confirmar las congela. */
    const pendingOptionsFor = (draft: ChargeDraft): SellerPaymentOptions | null =>
        pendingCharge && chargeFingerprintOf(pendingCharge.draft) === chargeFingerprintOf(draft)
            ? pendingCharge.options
            : null;
    /** «Es un cobro nuevo»: el siguiente envío estrena llave. */
    const discardPending = () => {
        dropPendingCharge(creditId);
        setPendingVersion((version) => version + 1);
    };
    const restartOffered =
        charge.isError &&
        (chargeErrorOf(charge.error).action === 'newKey' ||
            pendingRepeats >= PENDING_REPEATS_BEFORE_RESTART);
    return { charge, restartOffered, pendingOptionsFor, pendingCharge, discardPending };
}
